import { useFormikContext } from 'formik';
import { get } from 'lodash';

import { Box } from '@hivebrite/design-system/Box';
import { CardWithTitle } from '@hivebrite/design-system/Card';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import {
  Checkbox,
  Select,
  Text,
} from 'hb-react/shared/components/formik/components/inputs';
import { type AddressAttributes } from 'hb-react/shared/components/ShoppingCard/hooks/v2';

import SelectAddress from './SelectAddress';
import ShippingAddressForm from '../ShippingAddress';

const BillingAndShippingAddress = ({ userFieldsConfig, isShippable }) => {
  const { currentUser } = useDefaultAppContexts();
  const { values, setFieldValue } = useFormikContext<{
    receipt: {
      address_type: string;
      billing_address_attributes: AddressAttributes;
      shipping_address_attributes: AddressAttributes & {
        same_as_billing: boolean;
      };
    };
  }>();

  const getPostalPlaceholder = (addressType, field) =>
    get(userFieldsConfig, `postal_${addressType}_${field}_placeholder`);

  return (
    <CardWithTitle title={s__('NetworkEvents|Billing address')}>
      <Box
        display="grid"
        gridTemplateColumns={{
          _: '1fr',
          m: '1fr 1fr',
        }}
        style={{ gap: '24px' }}
      >
        <Box
          gridColumn={{
            _: 'auto',
            m: '1 / 3',
          }}
        >
          <SelectAddress
            // @ts-expect-error: types
            currentUser={currentUser}
            setSelectedAddress={(addressSchema) => {
              setFieldValue(
                'receipt[billing_address_attributes]',
                addressSchema,
              );
            }}
            values={values}
          />
        </Box>
        <Text
          name="receipt[billing_address_attributes][address_1]"
          label={I18n.t(
            'web.networks.events.receipt.billing_location.address_1',
          )}
          placeholder={
            getPostalPlaceholder(values?.receipt?.address_type, 'address_1') ||
            I18n.t('web.networks.events.receipt.billing_location.address_1')
          }
          validate={{ required: true }}
          wrapperProps={{ mb: 0 }}
        />
        <Text
          name="receipt[billing_address_attributes][address_2]"
          label={I18n.t(
            'web.networks.events.receipt.billing_location.address_2',
          )}
          placeholder={
            getPostalPlaceholder(values?.receipt?.address_type, 'address_2') ||
            I18n.t('web.networks.events.receipt.billing_location.address_2')
          }
          wrapperProps={{ mb: 0 }}
        />
        <Text
          name="receipt[billing_address_attributes][address_3]"
          label={I18n.t(
            'web.networks.events.receipt.billing_location.address_3',
          )}
          placeholder={
            getPostalPlaceholder(values?.receipt?.address_type, 'address_3') ||
            I18n.t('web.networks.events.receipt.billing_location.address_3')
          }
          wrapperProps={{ mb: 0 }}
        />
        <Text
          name="receipt[billing_address_attributes][city]"
          placeholder={I18n.t(
            'web.networks.events.receipt.billing_location.city',
          )}
          label={I18n.t('web.networks.events.receipt.billing_location.city')}
          validate={{ required: true }}
          wrapperProps={{ mb: 0 }}
        />
        <Text
          name="receipt[billing_address_attributes][state]"
          placeholder={I18n.t(
            'web.networks.events.receipt.billing_location.state',
          )}
          label={I18n.t('web.networks.events.receipt.billing_location.state')}
          wrapperProps={{ mb: 0 }}
        />
        <Text
          name="receipt[billing_address_attributes][postal_code]"
          placeholder={I18n.t(
            'web.networks.events.receipt.billing_location.postal_code',
          )}
          label={I18n.t(
            'web.networks.events.receipt.billing_location.postal_code',
          )}
          validate={{ required: true }}
          wrapperProps={{ mb: 0 }}
        />

        <Select
          type="countries"
          name="receipt[billing_address_attributes][country]"
          placeholder={I18n.t(
            'web.networks.events.receipt.billing_location.country',
          )}
          label={I18n.t('web.networks.events.receipt.billing_location.country')}
          inputHtml={{
            maxMenuHeight: 160,
          }}
          validate={{ required: true }}
        />
      </Box>
      {isShippable && (
        <>
          <Checkbox
            name="receipt[shipping_address_attributes][same_as_billing]"
            label={s__(
              'NetworkEvents|Billing address is same as shipping address',
            )}
          />
          {!values.receipt?.shipping_address_attributes?.same_as_billing && (
            <ShippingAddressForm withHeading />
          )}
        </>
      )}
    </CardWithTitle>
  );
};

export default BillingAndShippingAddress;
